/* eslint @typescript-eslint/no-empty-object-type: ["error", { allowInterfaces: "with-single-extends" }] */
// Object DTOs remain interfaces; their single Zod-derived base is the source of truth.
import { z } from "zod";
import { billingViewSchema, billingPriceBreakdownSchema, paidSubscriptionSnapshotSchema, isoUtcSchema, normalizeDecimalString, } from "./tariffs.js";
export const paymentIdSchema = z.string().uuid();
export const paymentVersionSchema = z.number().int().min(0).max(2147483647);
const count = paymentVersionSchema;
const positiveCount = count.min(1);
const instant = isoUtcSchema;
const rub = z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d*[1-9])?$/);
export const paymentMoneySchema = z
    .string()
    .max(4096)
    .regex(/^\d+(?:\.\d{1,2})?$/)
    .transform(normalizeDecimalString)
    .refine((v) => v !== "0", "Must be positive");
const money = rub.refine((v) => !v.includes(".") || (v.split(".")[1]?.length ?? 0) <= 2);
const text = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .superRefine((value, context) => {
    if ([...value].some((character) => {
        const code = character.codePointAt(0);
        return code !== undefined && (code <= 0x1f || code === 0x7f);
    })) {
        context.addIssue({
            code: z.ZodIssueCode.invalid_string,
            validation: "regex",
            message: "Invalid",
        });
    }
});
const note = text(4000);
const id = paymentIdSchema;
const version = paymentVersionSchema;
const nullableInstant = instant.nullable();
const token = z.string().min(1);
const request = { requestId: id };
const cas = { ...request, expectedVersion: version };
export function validPaymentInn(value) {
    if (!/^\d{10}$|^\d{12}$/.test(value))
        return false;
    const digit = (weights) => (weights.reduce((sum, weight, index) => sum + weight * Number(value[index]), 0) % 11) % 10;
    return value.length === 10
        ? digit([2, 4, 10, 3, 5, 9, 4, 6, 8]) === Number(value[9])
        : digit([7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === Number(value[10]) &&
            digit([3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === Number(value[11]);
}
function validBankAccount(account, prefix) {
    return (/^\d{20}$/.test(account) &&
        [...(prefix + account)].reduce((sum, digit, index) => sum + Number(digit) * [7, 1, 3][index % 3], 0) %
            10 ===
            0);
}
const inn = z.string().trim().refine(validPaymentInn, "Invalid INN checksum");
export const paymentPurchaseActionSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("ASSIGN"),
        tariffId: text(500),
        monthlyDialogs: positiveCount,
        months: positiveCount,
    })
        .strict(),
    z
        .object({
        kind: z.literal("RENEW"),
        tariffId: text(500),
        monthlyDialogs: positiveCount,
        months: positiveCount,
    })
        .strict(),
    z
        .object({ kind: z.literal("UPGRADE"), tariffId: text(500), monthlyDialogs: positiveCount })
        .strict(),
    z.object({ kind: z.literal("TOPUP"), dialogs: positiveCount }).strict(),
]);
export const billingPayerFieldsSchema = z
    .object({
    kind: z.enum(["ORGANIZATION", "IP"]),
    name: text(500),
    inn,
    kpp: z
        .string()
        .trim()
        .regex(/^\d{9}$/)
        .nullable(),
    address: text(2000),
    email: z.string().trim().email().nullable(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.inn.length !== (v.kind === "ORGANIZATION" ? 10 : 12))
        ctx.addIssue({
            code: "custom",
            path: ["inn"],
            message: "INN length does not match payer kind",
        });
    if (v.kind === "IP" && v.kpp !== null)
        ctx.addIssue({ code: "custom", path: ["kpp"], message: "IP has no KPP" });
});
export const paymentRecipientFieldsSchema = z
    .object({
    name: text(500),
    inn: inn.refine((v) => v.length === 12),
    taxStatus: z.literal("NPD"),
    address: text(2000),
    email: z.string().trim().email(),
    bankName: text(500),
    bik: z
        .string()
        .trim()
        .regex(/^\d{9}$/),
    bankAccount: z
        .string()
        .trim()
        .regex(/^\d{20}$/),
    correspondentAccount: z
        .string()
        .trim()
        .regex(/^\d{20}$/),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!validBankAccount(v.bankAccount, v.bik.slice(-3)))
        ctx.addIssue({
            code: "custom",
            path: ["bankAccount"],
            message: "Invalid bank account checksum",
        });
    if (!validBankAccount(v.correspondentAccount, "0" + v.bik.slice(4, 6)))
        ctx.addIssue({
            code: "custom",
            path: ["correspondentAccount"],
            message: "Invalid correspondent account checksum",
        });
});
export const PAYMENT_REVIEW_REASONS = [
    "LATE_PAYMENT",
    "PARTIAL_PAYMENT",
    "OVERPAYMENT",
    "EXTRA_PAYMENT",
    "PAYER_MISMATCH",
    "PAYER_UNKNOWN",
    "SOURCE_CHANGED",
    "PERIOD_EXPIRED",
    "PAYMENT_CORRECTION",
    "MANUAL_REVIEW",
];
export const paymentReviewReasonSchema = z.enum(PAYMENT_REVIEW_REASONS);
export const paymentBankIdentitySchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("BANK_OPERATION"), operationId: text(200) }).strict(),
    z.object({ kind: z.literal("STATEMENT_ROW"), statementId: text(200), rowId: text(200) }).strict(),
]);
export const billingPayerSchema = z
    .object({
    ...billingPayerFieldsSchema.innerType().shape,
    id,
    version,
    archivedAt: nullableInstant,
    createdAt: instant,
    updatedAt: instant,
})
    .strict();
export const paymentSettingsSchema = z.object({ version, defaultPayerId: id.nullable() }).strict();
export const paymentSegmentSchema = z
    .object({
    id,
    termsVersion: version,
    startsAt: instant,
    endsAt: instant,
    originalSnapshot: paidSubscriptionSnapshotSchema,
    effectiveSnapshot: paidSubscriptionSnapshotSchema,
})
    .strict();
export const paymentBillingSchema = z
    .object({
    billing: billingViewSchema,
    segmentEndsAt: nullableInstant,
    paidThroughAt: nullableInstant,
    futureSegments: z.array(paymentSegmentSchema),
    allowedActions: z
        .object({ assign: z.boolean(), renew: z.boolean(), upgrade: z.boolean(), topup: z.boolean() })
        .strict(),
    upgradeMinimums: z.array(z.object({ tariffId: text(500), monthlyDialogs: positiveCount.nullable() }).strict()),
})
    .strict();
export const paymentPayersPageSchema = z
    .object({
    items: z.array(billingPayerSchema),
    nextCursor: token.nullable(),
    settings: paymentSettingsSchema,
    defaultPayer: billingPayerSchema.nullable(),
})
    .strict();
export const paymentRecipientSchema = z
    .object({
    ...paymentRecipientFieldsSchema.innerType().shape,
    id,
    createdAt: instant,
    createdBy: id,
})
    .strict();
export const paymentDocumentSchema = z
    .object({
    id,
    state: z.enum(["WRITING", "READY", "FAILED"]),
    version,
    size: count.nullable(),
    sha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
    errorCode: text(200).nullable(),
})
    .strict();
export const paymentOfferSchema = z
    .object({
    versionId: id,
    title: text(500),
    editionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    url: z.string().url(),
})
    .strict();
export const paymentUpgradeSegmentQuoteSchema = z
    .object({
    subscriptionId: id,
    termsVersion: version,
    startsAt: instant,
    endsAt: instant,
    oldBaseMonthlyRub: rub,
    newBaseMonthlyRub: rub,
    remainingMs: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
})
    .strict();
export const paymentQuoteInputSchema = z
    .object({
    action: paymentPurchaseActionSchema,
    expectedCatalogVersion: version,
    expectedBillingVersion: version,
})
    .strict();
export const paymentQuoteSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    action: paymentPurchaseActionSchema,
    calculatedAt: instant,
    totalRub: rub,
    breakdown: billingPriceBreakdownSchema,
    segments: z.array(paymentUpgradeSegmentQuoteSchema),
    paidThroughAt: nullableInstant,
    periodStartsAt: nullableInstant,
    periodEndsAt: nullableInstant,
    targetSnapshot: paidSubscriptionSnapshotSchema,
    startRule: z.enum([
        "ACTIVATION",
        "PAID_CHAIN_END_OR_ACTIVATION",
        "CONFIRMATION",
        "CURRENT_PERIOD",
    ]),
})
    .strict();
export const paymentQuoteSchema = z
    .object({
    action: paymentPurchaseActionSchema,
    accountId: id,
    catalogVersion: version,
    billingVersion: version,
    calculatedAt: instant,
    expiresAt: instant,
    quoteToken: token,
    totalRub: rub,
    breakdown: billingPriceBreakdownSchema,
    segments: z.array(paymentUpgradeSegmentQuoteSchema),
    paidThroughAt: nullableInstant,
    periodEndsAt: nullableInstant,
    activeRecipientVersion: id.nullable(),
    invoiceAvailability: z.enum([
        "READY",
        "RECIPIENT_NOT_CONFIGURED",
        "ZERO_AMOUNT",
        "CALENDAR_UNAVAILABLE",
        "OFFER_NOT_CONFIGURED",
    ]),
    estimatedInvoiceExpiresAt: nullableInstant,
    offer: paymentOfferSchema.nullable(),
})
    .strict();
export const invoiceCreateInputSchema = z
    .object({
    ...request,
    quoteToken: token,
    payerId: id,
    expectedPayerVersion: version,
    expectedRecipientVersion: id,
})
    .strict();
export const paymentAllocationSchema = z
    .object({ paymentId: id, amountRub: paymentMoneySchema })
    .strict();
export const invoiceApplicationSchema = z
    .object({
    billingOperationId: id,
    appliedAt: instant,
    effectiveAt: instant,
    accessStartsAt: instant,
    paidThroughAt: nullableInstant,
    monthlyDialogs: count,
    topupDialogs: count,
    allocations: z.array(paymentAllocationSchema),
})
    .strict();
export const invoiceListItemSchema = z
    .object({
    id,
    version,
    number: text(64),
    issuedAt: instant,
    expiresAt: instant,
    action: paymentPurchaseActionSchema,
    description: text(10000),
    totalRub: rub,
    currency: z.literal("RUB"),
    status: z.enum([
        "AWAITING_PAYMENT",
        "PAYMENT_REPORTED",
        "PAID_AWAITING_ACTIVATION",
        "ACTIVATED",
        "EXPIRED",
        "REQUIRES_REVIEW",
        "SUPERSEDED",
    ]),
    document: paymentDocumentSchema,
    paymentReportedAt: nullableInstant,
    canReportPayment: z.boolean(),
    paymentConfirmedAt: nullableInstant,
    activationDueAt: nullableInstant,
    application: invoiceApplicationSchema.nullable(),
    receivedRub: money,
    allocatedRub: money,
    unallocatedRub: money,
    reviewRequired: z.boolean(),
    supersededBy: z
        .object({ id, number: text(64) })
        .strict()
        .nullable(),
})
    .strict();
export const paymentSchema = z
    .object({
    id,
    version,
    invoiceId: id,
    amountRub: paymentMoneySchema,
    receivedAt: instant,
    confirmedAt: instant,
    payerName: text(500),
    payerInn: inn.nullable(),
    recordState: z.enum(["VALID", "ERROR"]),
    reviewRequired: z.boolean(),
    allocatedRub: money,
    unallocatedRub: money,
    currentReceipt: paymentDocumentSchema.nullable(),
})
    .strict();
export const paymentActualPayerSchema = z.object({ name: text(500), inn: inn.nullable() }).strict();
export const paymentDraftSchema = z
    .object({
    amountRub: paymentMoneySchema,
    receivedAt: instant,
    currency: z.literal("RUB"),
    bankIdentity: paymentBankIdentitySchema,
    payer: paymentActualPayerSchema,
    note: note.nullable(),
})
    .strict();
export const paymentRecordInputSchema = z
    .object({ ...cas, payment: paymentDraftSchema, mode: z.enum(["CONFIRM", "RECORD_FOR_REVIEW"]) })
    .strict();
export const invoiceApplicationPreviewInputSchema = z
    .object({ expectedVersion: version, allocations: z.array(paymentAllocationSchema).min(1) })
    .strict();
export const invoiceUpgradePreviewInputSchema = z
    .object({
    expectedVersion: version,
    allocations: z.array(paymentAllocationSchema),
    newPayment: paymentDraftSchema.nullable(),
    newPaymentAllocationRub: paymentMoneySchema.nullable(),
})
    .strict();
export const invoiceApplicationPreviewSchema = z
    .object({
    previewToken: token,
    expiresAt: instant,
    invoiceVersion: version,
    billingVersion: version,
    totalRub: rub,
    allocations: z.array(paymentAllocationSchema),
    newPayment: paymentDraftSchema.nullable(),
    newPaymentAllocationRub: paymentMoneySchema.nullable(),
    startRule: paymentQuoteSnapshotSchema.shape.startRule,
    accessStartsAt: nullableInstant,
    paidThroughAt: nullableInstant,
    monthlyDialogs: count,
    topupDialogs: count,
    remainingUnallocatedRub: money,
})
    .strict();
export const invoiceApplicationConfirmInputSchema = z
    .object({ ...cas, previewToken: token })
    .strict();
export const paymentAllowedCommandsSchema = z
    .object({
    recordPayment: z.boolean(),
    previewApplication: z.boolean(),
    previewUpgrade: z.boolean(),
    previewReview: z.boolean(),
    retryDocument: z.boolean(),
})
    .strict();
export const paymentReviewSchema = z
    .object({
    id,
    createdAt: instant,
    adminId: id,
    reasonCode: paymentReviewReasonSchema,
    note: note,
    decision: z.enum(["OPEN", "ACCEPT_ORIGINAL", "REPLACE_INVOICE"]),
    customerAgreement: note.nullable(),
    paymentIds: z.array(id),
    replacementInvoiceId: id.nullable(),
    provisionAllowed: z.boolean(),
})
    .strict();
export const paymentCorrectionInputSchema = z
    .object({ ...cas, reason: note, replacement: paymentDraftSchema.nullable() })
    .strict();
export const paymentReceiptInputSchema = z.object({ ...cas, reason: note.nullable() }).strict();
export const paymentRecipientPreviewInputSchema = z
    .object({ expectedActiveVersionId: id.nullable(), fields: paymentRecipientFieldsSchema })
    .strict();
export const paymentRecipientPreviewSchema = z
    .object({
    previewToken: token,
    expiresAt: instant,
    expectedActiveVersionId: id.nullable(),
    fields: paymentRecipientFieldsSchema,
})
    .strict();
export const paymentRecipientConfirmInputSchema = z
    .object({ ...request, expectedActiveVersionId: id.nullable(), previewToken: token })
    .strict();
export const paymentReviewReplacementSchema = z
    .object({
    action: paymentPurchaseActionSchema,
    expectedCatalogVersion: version,
    expectedBillingVersion: version,
    payerId: id,
    expectedPayerVersion: version,
    expectedRecipientVersion: id,
})
    .strict();
export const paymentReviewPreviewInputSchema = z
    .object({
    expectedVersion: version,
    decision: z.enum(["ACCEPT_ORIGINAL", "REPLACE_INVOICE"]),
    reasonCode: paymentReviewReasonSchema,
    note,
    customerAgreement: note.nullable(),
    paymentIds: z.array(id),
    allocations: z.array(paymentAllocationSchema),
    replacement: paymentReviewReplacementSchema.nullable(),
})
    .strict();
export const paymentReviewPreviewSchema = z
    .object({
    previewToken: token,
    expiresAt: instant,
    invoiceVersion: version,
    decision: z.enum(["ACCEPT_ORIGINAL", "REPLACE_INVOICE"]),
    replacementQuote: paymentQuoteSchema.nullable(),
    allocations: z.array(paymentAllocationSchema),
    unallocatedRub: money,
    provisionAllowed: z.boolean(),
})
    .strict();
export const invoiceDetailSchema = invoiceListItemSchema
    .extend({
    payer: billingPayerFieldsSchema,
    recipient: paymentRecipientFieldsSchema,
    offer: paymentOfferSchema,
    purchase: paymentQuoteSnapshotSchema,
    payments: z.array(paymentSchema),
})
    .strict();
export const adminPaymentSchema = paymentSchema
    .extend({
    bankIdentity: paymentBankIdentitySchema,
    payer: paymentActualPayerSchema,
    note: note.nullable(),
    correctsPaymentId: id.nullable(),
})
    .strict();
export const adminInvoiceDetailSchema = invoiceDetailSchema
    .extend({
    payments: z.array(adminPaymentSchema),
    reviews: z.array(paymentReviewSchema),
    allowedCommands: paymentAllowedCommandsSchema,
    billing: paymentBillingSchema,
})
    .strict();
export const invoicePageSchema = z
    .object({ items: z.array(invoiceListItemSchema), nextCursor: token.nullable() })
    .strict();
export const adminPaymentsPageSchema = invoicePageSchema
    .extend({ billing: paymentBillingSchema })
    .strict();
export const paymentCommandResultSchema = z
    .object({
    invoice: adminInvoiceDetailSchema,
    billing: paymentBillingSchema,
    paymentId: id.optional(),
})
    .strict();
export const paymentReviewResultSchema = z
    .object({
    invoice: adminInvoiceDetailSchema,
    replacementInvoice: adminInvoiceDetailSchema.nullable(),
    billing: paymentBillingSchema,
})
    .strict();
export const paymentPayerCreateInputSchema = z
    .object({ ...request, fields: billingPayerFieldsSchema })
    .strict();
export const paymentPayerPatchInputSchema = z
    .object({ ...cas, fields: billingPayerFieldsSchema.optional(), archived: z.boolean().optional() })
    .strict()
    .refine((v) => v.fields !== undefined || v.archived !== undefined, "At least one change required");
export const paymentDefaultPayerInputSchema = z.object({ ...cas, payerId: id.nullable() }).strict();
export const paymentPageQuerySchema = z
    .object({ cursor: token.optional(), limit: z.coerce.number().int().min(1).max(100).default(20) })
    .strict();
export const paymentPayersQuerySchema = paymentPageQuerySchema
    .extend({ archived: z.enum(["active", "archived", "all"]).default("active") })
    .strict();
export const paymentInvoicesQuerySchema = paymentPageQuerySchema
    .extend({
    status: invoiceListItemSchema.shape.status.optional(),
    number: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[0-9-]+$/)
        .optional(),
})
    .strict();
export const paymentPdfQuerySchema = z
    .object({ disposition: z.enum(["inline", "attachment"]).default("attachment") })
    .strict();
export const paymentReviewConfirmInputSchema = invoiceApplicationConfirmInputSchema;
export const invoiceReportPaidInputSchema = z.object(cas).strict();
//# sourceMappingURL=payments.js.map